/**
 * useLiveStats Hook (Web Wrapper)
 *
 * Thin wrapper around the shared useLiveStats hook.
 * Adapts the web-specific signature (positional args, web TrackedParlay type)
 * to the shared hook's options-based interface.
 *
 * Types: Import LiveLegStats, LiveGameScores, QuarterScore, etc. directly
 * from '@parlay-picker/shared/hooks' — this file does NOT re-export them.
 */

import {
  extractLiveGameIds,
  findPlayerByName,
  getStatValue,
  parseGameStatus,
  type UseLiveStatsResult,
  useLiveStats as useSharedLiveStats,
} from '@parlay-picker/shared/hooks';
import { logError } from '../services/errorTracking';
import type { TrackedParlay } from './useTrackedParlays';

// Export the result type since consumers reference it from this file
export type { UseLiveStatsResult };

// Export shared helpers so existing test imports continue to work
export { parseGameStatus, findPlayerByName, getStatValue, extractLiveGameIds };

/**
 * Web-specific wrapper around the shared useLiveStats hook.
 * Converts the positional (parlays, callback) signature to the shared
 * options object and passes baseUrl='' for same-origin SSE.
 */
export function useLiveStats(
  parlays: TrackedParlay[],
  onSettlementComplete?: () => void,
): UseLiveStatsResult {
  return useSharedLiveStats({
    parlays,
    baseUrl: '',
    onSettlementComplete,
    onError: logError,
  });
}
